import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Star, Flame, ArrowRight, Trophy, ChevronDown } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import type { ModuleId } from '../../content';
import { getModuleIcon, getFamiliesForModule, getSkillsForModule, getFamiliesForModuleSkill } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { Card } from '../ui/Card';
import { GrowthThisWeekSection } from './GrowthThisWeekSection';
import { MilestonesSection } from './MilestonesSection';
import { PersonalRecordsSection } from './PersonalRecordsSection';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { getGrowthStage, getFamilyMasteryKey } from '../../mastery';
import type { AssessmentReport } from '../../assessment';
const MODULE_COLORS: Record<ModuleId, {
  border: string;
  fill: string;
  iconBg: string;
  iconText: string;
  gradientFrom: string;
  gradientTo: string;
}> = {
  Metriek: {
    border: 'border-blue-200',
    fill: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100/30'
  },
  Getalbegrip: {
    border: 'border-indigo-200',
    fill: 'bg-indigo-500',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100/30'
  },
  Basisrekenen: {
    border: 'border-orange-200',
    fill: 'bg-orange-500',
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600',
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100/30'
  },
  Tijd: {
    border: 'border-sky-200',
    fill: 'bg-sky-500',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    gradientFrom: 'from-sky-50',
    gradientTo: 'to-sky-100/30'
  },
  Geld: {
    border: 'border-yellow-200',
    fill: 'bg-yellow-500',
    iconBg: 'bg-yellow-100',
    iconText: 'text-yellow-600',
    gradientFrom: 'from-yellow-50',
    gradientTo: 'to-yellow-100/30'
  },
  SpellingWW: {
    border: 'border-fuchsia-200',
    fill: 'bg-fuchsia-500',
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600',
    gradientFrom: 'from-fuchsia-50',
    gradientTo: 'to-fuchsia-100/30'
  },
  SpellingRegel: {
    border: 'border-amber-200',
    fill: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    gradientFrom: 'from-amber-50',
    gradientTo: 'to-amber-100/30'
  },
  Taalbegrip: {
    border: 'border-purple-200',
    fill: 'bg-purple-500',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100/30'
  },
  Studievaardig: {
    border: 'border-teal-200',
    fill: 'bg-teal-500',
    iconBg: 'bg-teal-100',
    iconText: 'text-teal-600',
    gradientFrom: 'from-teal-50',
    gradientTo: 'to-teal-100/30'
  },
  Topo: {
    border: 'border-cyan-200',
    fill: 'bg-cyan-500',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-600',
    gradientFrom: 'from-cyan-50',
    gradientTo: 'to-cyan-100/30'
  }
};
const FALLBACK_COLORS = {
  border: 'border-slate-200',
  fill: 'bg-slate-500',
  iconBg: 'bg-slate-100',
  iconText: 'text-slate-600',
  gradientFrom: 'from-slate-50',
  gradientTo: 'to-slate-100/30'
};
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  // Modules with mastery data
  const activeModules = useMemo(() => {
    return MODULES.filter(module => {
      const families = getFamiliesForModule(module.id);
      return families.length > 0 && families.some(f => {
        const key = getFamilyMasteryKey(f.familyId, selectedGroup);
        return state.familyMastery?.[key] !== undefined;
      });
    });
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    if (activeModules.length === 0) return 0;
    let masterySum = 0;
    activeModules.forEach(m => {
      masterySum += getModuleMasteryForGroup(m.id);
    });
    return Math.round(masterySum / activeModules.length);
  }, [activeModules, getModuleMasteryForGroup]);
  const overallGrowthStage = getGrowthStage(overallMastery);
  return <PageContainer className="space-y-5 sm:space-y-8 pb-safe px-3 sm:px-6" data-id="element-1821">
      {/* ── 1. Hero ── */}
      <motion.div variants={itemVariants} data-id="element-1822">
        <PageHero title="Mijn Voortgang" subtitle={activeModules.length === 0 ? 'Nog niet gestart' : 'Ontdek welke vaardigheden je al beheerst'} gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1823">
          <div className="flex flex-wrap gap-2 items-center" data-id="element-1824">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-1.5 pr-3 sm:p-2 sm:pr-4 flex items-center gap-2.5 min-h-[44px]" data-id="element-1825">
              <div className="relative w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0" data-id="element-1826">
                <svg className="w-9 h-9 sm:w-10 sm:h-10 transform -rotate-90" data-id="element-1827">
                  <circle cx="50%" cy="50%" r="16" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" data-id="element-1828" />
                  <circle cx="50%" cy="50%" r="16" fill="none" stroke="white" strokeWidth="4" strokeDasharray="100.5" strokeDashoffset={100.5 - overallMastery / 100 * 100.5} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1829" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1830">
                  <span className="text-[10px] font-black text-white" data-id="element-1831">
                    {overallMastery}%
                  </span>
                </div>
              </div>
              <div className="leading-tight" data-id="element-1832">
                <p className="text-[10px] text-white/80 font-medium" data-id="element-1833">
                  Jij bent een
                </p>
                <p className="text-xs sm:text-sm font-bold text-white" data-id="element-1834">
                  {overallGrowthStage.emoji} {overallGrowthStage.label}
                </p>
              </div>
            </div>
            <HeroStatPill icon={Flame} data-id="element-1835">{currentStreak} dagen</HeroStatPill>
          </div>
        </PageHero>
      </motion.div>

      {/* ── 2. Deze week gegroeid ── */}
      <motion.div variants={itemVariants} data-id="element-1836">
        <GrowthThisWeekSection state={state} selectedGroup={selectedGroup} onNavigate={onNavigate} data-id="element-1837" />
      </motion.div>

      {/* ── 3. Mijlpalen ── */}
      <motion.div variants={itemVariants} data-id="element-1838">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1839" />
      </motion.div>

      {/* ── 4. Vaardigheden per Vak ── */}
      {activeModules.length > 0 && <motion.div variants={itemVariants} data-id="element-1840">
          <div className="space-y-3" data-id="element-1841">
            <div className="flex items-center gap-3 mb-2" data-id="element-1842">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-1843">
                <Star className="w-5 h-5 text-blue-600" data-id="element-1844" />
              </div>
              <h2 className="text-xl font-black text-slate-800" data-id="element-1845">
                Vaardigheden per Vak
              </h2>
            </div>
            {activeModules.map(module => <ModuleProgressZone key={module.id} module={module} families={getFamiliesForModule(module.id)} state={state} selectedGroup={selectedGroup} data-id="element-1846" />)}
          </div>
        </motion.div>}

      {/* ── 5. Persoonlijke Records ── */}
      <motion.div variants={itemVariants} data-id="element-1847">
        <PersonalRecordsSection fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} data-id="element-1848" />
      </motion.div>

      {/* ── 6. Collectie Link ── */}
      <motion.div variants={itemVariants} data-id="element-1849">
        <button onClick={() => onNavigate('collection')} className="w-full text-left active:scale-[0.99] transition-transform" data-id="element-1850">
          <Card className="p-4 sm:p-5 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl flex items-center gap-4 hover:shadow-md hover:border-amber-300 transition-all group shadow-sm min-h-[64px]" data-id="element-1851">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-1852">
              <Trophy className="w-6 h-6 text-white" data-id="element-1853" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1854">
              <h3 className="font-black text-slate-800 text-lg" data-id="element-1855">
                Mijn Collectie
              </h3>
              <p className="text-xs text-slate-600 font-medium" data-id="element-1856">
                Bekijk al je prestaties en Djay-varianten
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-1857">
              <ArrowRight className="w-5 h-5 text-amber-500 group-hover:translate-x-0.5 transition-transform" data-id="element-1858" />
            </div>
          </Card>
        </button>
      </motion.div>
    </PageContainer>;
}
// ── Module Progress Zone (compact) ────────────────────────────────────
function ModuleProgressZone({
  module,
  families,
  state,
  selectedGroup
}: {
  module: any;
  families: any[];
  state: AppState;
  selectedGroup: GroupId;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = MODULE_COLORS[module.id as ModuleId] || FALLBACK_COLORS;
  const ModuleIcon = getModuleIcon(module.icon);
  const skills = getSkillsForModule(module.id);
  let totalScore = 0,
    count = 0,
    totalAttempts = 0;
  families.forEach(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    const data = state.familyMastery?.[key];
    if (data) {
      totalScore += data.masteryScore;
      count++;
      totalAttempts += data.attempts || 0;
    }
  });
  const moduleMastery = count > 0 ? Math.round(totalScore / count) : 0;
  const growthStage = getGrowthStage(moduleMastery);
  // Map module to a color class for the left border
  const colorMap: Record<string, string> = {
    Metriek: 'border-l-blue-500',
    Getalbegrip: 'border-l-indigo-500',
    Basisrekenen: 'border-l-orange-500',
    Tijd: 'border-l-sky-500',
    Geld: 'border-l-yellow-500',
    SpellingWW: 'border-l-fuchsia-500',
    SpellingRegel: 'border-l-amber-500',
    Taalbegrip: 'border-l-purple-500',
    Studievaardig: 'border-l-teal-500',
    Topo: 'border-l-cyan-500'
  };
  const borderClass = colorMap[module.id] || 'border-l-slate-500';
  return <div className={`rounded-2xl border-2 ${colors.border} border-l-4 ${borderClass} bg-white shadow-sm overflow-hidden`} data-id="element-1859">
      <button onClick={() => setIsExpanded(!isExpanded)} className={`w-full px-3 py-3 sm:px-4 sm:py-3.5 flex items-center gap-3 bg-gradient-to-r ${colors.gradientFrom} ${colors.gradientTo} hover:opacity-90 active:scale-[0.99] transition-all min-h-[56px]`} data-id="element-1860">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.iconBg} flex-shrink-0`} data-id="element-1861">
          <ModuleIcon className={`w-4.5 h-4.5 ${colors.iconText}`} data-id="element-1862" />
        </div>
        <div className="flex-1 min-w-0 text-left" data-id="element-1863">
          <h3 className="text-sm sm:text-base font-black text-slate-900 leading-tight" data-id="element-1864">
            {module.title}
          </h3>
          <div className="flex items-center gap-2 mt-0.5" data-id="element-1865">
            <span className="text-[11px] font-medium text-slate-500" data-id="element-1866">
              {growthStage.emoji} {count} vaardigheden
            </span>
            {totalAttempts > 0 && <span className="text-[9px] font-bold text-slate-400 bg-white/50 px-1.5 py-0.5 rounded" data-id="element-1867">
                {totalAttempts}x geoefend
              </span>}
            <div className="w-16 h-1.5 bg-white/50 rounded-full overflow-hidden ml-auto sm:ml-0" data-id="element-1868">
              <div className={`h-full ${colors.fill} rounded-full`} style={{
              width: `${moduleMastery}%`
            }} data-id="element-1869" />
            </div>
          </div>
        </div>
        <div className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-white/80 flex items-center justify-center transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1870">
          <ChevronDown className={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${colors.iconText}`} data-id="element-1871" />
        </div>
      </button>

      <AnimatePresence data-id="element-1872">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="border-t border-slate-100" data-id="element-1873">
            <div className="p-3 sm:p-4 space-y-4" data-id="element-1874">
              {skills.map(skill => {
            const skillFamilies = getFamiliesForModuleSkill(module.id, skill);
            const activeFamilies = skillFamilies.filter(f => {
              const key = getFamilyMasteryKey(f.familyId, selectedGroup);
              return state.familyMastery?.[key] !== undefined;
            });
            if (activeFamilies.length === 0) return null;
            return <div key={skill} className="space-y-1.5" data-id="element-1875">
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1" data-id="element-1876">
                      {skill}
                    </h4>
                    <div className="space-y-1" data-id="element-1877">
                      {activeFamilies.map(f => {
                  const key = getFamilyMasteryKey(f.familyId, selectedGroup);
                  const data = state.familyMastery?.[key];
                  const score = data?.masteryScore || 0;
                  const gs = getGrowthStage(score);
                  // Calculate 7-day delta
                  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  let recentCorrect = 0;
                  data?.history?.forEach(h => {
                    if (h.timestamp >= weekAgo && h.isCorrect) recentCorrect++;
                  });
                  return <div key={f.familyId} className="flex items-center gap-2 px-2 py-2.5 rounded-lg bg-slate-50/80 hover:bg-slate-100 transition-colors min-h-[44px]" data-id="element-1878">
                            <span className="text-sm flex-shrink-0" data-id="element-1879">
                              {gs.emoji}
                            </span>
                            <p className="text-xs font-bold text-slate-700 flex-1 min-w-0 truncate" data-id="element-1880">
                              {f.description || f.learningGoal}
                            </p>
                            {recentCorrect > 0 && <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] font-black flex-shrink-0" data-id="element-1881">
                                ↑ {recentCorrect} goed
                              </div>}
                            <div className="w-16 sm:w-20 h-2 bg-slate-200 rounded-full overflow-hidden flex-shrink-0 ml-2" data-id="element-1882">
                              <div className={`h-full ${colors.fill} rounded-full`} style={{
                        width: `${score}%`
                      }} data-id="element-1883" />
                            </div>
                          </div>;
                })}
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}